import React from 'react';
import { CountyDropDown, DropDownBoxComponentSpacing, StateDropDown, TextBox } from '@apply/components';
import { Address } from '../types';
import { asInput } from './inputValue';

interface AddressFieldsProps {
    idPrefix: string; // Prefix for control ids so multiple address blocks can coexist on a page
    value: Address;
    onChange: (patch: Partial<Address>) => void;
    requiredFields?: boolean;
    readOnly?: boolean;
}

export const AddressFields: React.FC<AddressFieldsProps> = ({
    idPrefix,
    value,
    onChange,
    requiredFields = false,
    readOnly = false,
}) => {
    const update = <K extends keyof Address>(key: K, fieldValue: Address[K]) =>
        onChange({ [key]: fieldValue } as Partial<Address>);

    return (
        <>
            <TextBox
                id={`${idPrefix}Street1`}
                value={asInput(value.street1)}
                onChange={(v) => update('street1', v)}
                label='Street Address'
                required={requiredFields}
                readOnly={readOnly}
                maxLength={50}
            />
            <TextBox
                id={`${idPrefix}Street2`}
                value={asInput(value.street2)}
                onChange={(v) => update('street2', v)}
                label='Street Address Line 2'
                readOnly={readOnly}
                maxLength={50}
            />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ flex: '1 1 100%' }}>
                    <TextBox
                        id={`${idPrefix}City`}
                        value={asInput(value.city)}
                        onChange={(v) => update('city', v)}
                        label='City'
                        required={requiredFields}
                        readOnly={readOnly}
                        maxLength={50}
                    />
                </div>
                <div style={{ flex: '1 1 140px', minWidth: '140px' }}>
                    <TextBox
                        id={`${idPrefix}ZipCode`}
                        value={asInput(value.zipCode)}
                        onChange={(v) => update('zipCode', v)}
                        label='Zip Code'
                        required={requiredFields}
                        readOnly={readOnly}
                        maxLength={10}
                    />
                </div>
                <div style={{ flex: '1 1 272px', minWidth: '272px' }}>
                    <StateDropDown
                        id={`${idPrefix}State`}
                        value={asInput(value.state)}
                        onChange={(v) => update('state', v)}
                        label='State'
                        required={requiredFields}
                        readOnly={readOnly}
                        componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                    />
                </div>
            </div>

            <CountyDropDown
                id={`${idPrefix}County`}
                value={asInput(value.county)}
                onChange={(v) => update('county', v)}
                label='County'
                required={requiredFields}
                readOnly={readOnly}
                selectedState={value.state}
            />
        </>
    );
};
